/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from "react";
import { Preposition } from "@iventis/types";
import { styled } from "@iventis/styles";
import { ZERO_TIME_SPAN } from "@iventis/utilities";
import { TimeSelector } from "./time-selector";
import { AutocompleteWithLoading } from "../autocomplete";

// Time span selector will allow the user to select a positive or negitive +/- hh:mm:ss
export type TimeWithPrepositionSelectorProps = {
    className?: string;
    disabled?: boolean;
    onChange?: (timeSpan: string) => void;
    name?: string;
    timeSpanValue?: string;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    error?: boolean;
    beforePrepositionLabel: string; // Example "After session start (Should be passed in translated)"
    afterPrepositionLabel: string; // Example "Before session start (Should be passed in translated)"
};

export const TimeWithPrepositionSelector: React.FunctionComponent<TimeWithPrepositionSelectorProps> = ({
    name,
    timeSpanValue,
    onChange,
    error,
    onBlur,
    disabled,
    beforePrepositionLabel,
    afterPrepositionLabel,
}) => {
    const offsetOptions: { [key: string]: string } = {
        [Preposition.After]: afterPrepositionLabel,
        [Preposition.Before]: beforePrepositionLabel,
    };
    const initialTime = useMemo(
        // The time form value must be the absolute value off the offset, so we remove the "-" if it's a negative offset
        () => (timeSpanValue?.startsWith("-") ? (timeSpanValue as string)?.slice(1) : timeSpanValue || ZERO_TIME_SPAN),
        []
    );
    const initialPreposition = timeSpanValue && (timeSpanValue as string)[0] === "-" ? Preposition.Before : Preposition.After;

    const [state, setState] = useState({
        time: initialTime,
        preposition: initialPreposition,
    });
    const { time, preposition } = state;

    const onTimeSpanChange = (time: string) => {
        setState({ ...state, time });
        onChange(combineSignAndTimeSpan(time, preposition));
    };
    const onPrepositionChange = (preposition: Preposition) => {
        setState({ ...state, preposition });
        onChange(combineSignAndTimeSpan(time, preposition));
    };

    const combineSignAndTimeSpan = (timeStamp: string, preposition: Preposition) => (preposition === Preposition.Before ? `-${timeStamp}` : timeStamp);

    return (
        <StyledTimeSpan>
            <TimeSelector
                disabled={disabled}
                name={name}
                value={time}
                onChange={(e) => onTimeSpanChange(e.target.value)}
                error={error}
                helperText={error ? "hh:mm:ss" : null}
                onBlur={onBlur}
            />
            <AutocompleteWithLoading
                value={preposition}
                id="after-before-selector"
                options={[Preposition.Before, Preposition.After]}
                onChange={(option: Preposition) => onPrepositionChange(option)}
                getOptionLabel={(option: Preposition) => offsetOptions[option]}
                getOptionSelected={(option: Preposition, selected: Preposition) => option === selected}
                loadingOptions={false}
                disableClearable
                disabled={disabled}
                style={{ flexGrow: 1, minWidth: "200px" }}
            />
        </StyledTimeSpan>
    );
};

export const StyledTimeSpan = styled.div`
    display: flex;
    gap: 0.2rem;
`;
